import _ from 'lodash';
import * as React from 'react';
import {Table, Segment, Header} from 'semantic-grab-ui/src';
import {flatten, spanOn, getDefaultNodeName, getNodePopupContent} from "omega-rtc/utils/trident-node-utils";
import {Campaign} from "types/ts/campaigns";
import {RuleType} from "types/ts/rule-types";
import {NodesWarnings} from 'types/trident-campaigns';
import {nodeTypes} from 'data/trident-types';
import {
  ScenariosShow, ConditionShow, CountConditionShow, CounterShow,
  DelayShow, ActionShow, TimeShow, ExperimentShow, ExperimentTreatmentShow, EnumerationShow,
  RandomizerShow,
} from 'components/form/trident-campaigns';

interface TridentCampaignSummaryProps {
  campaign: Campaign,
  listRuleTypes: RuleType[],
  warnings?: NodesWarnings,
  hideUsage?: boolean,
}

export default class TridentCampaignSummary extends React.Component<TridentCampaignSummaryProps> {

  showData = (nodeKey: string, model: Campaign, parents: string[]) => {
    const defaultValue = model.nodes[nodeKey].name.length > 0
    ? `- ${model.nodes[nodeKey].name}`
    : getDefaultNodeName(nodeKey, model.nodes[nodeKey].type, _.get(model, 'nodesOccMap', {}))
    const nodeInfo = getNodePopupContent(model, 'name', nodeKey, defaultValue, true);
    return (
      <Segment>
        <Header size={'tiny'}>
          {`${model.nodes[nodeKey].type.toUpperCase()}`}
          &nbsp;{nodeInfo['name']}
          <span style={{fontWeight:'lighter', color:'#00b140'}} >
            {nodeInfo['value']}
          </span>
        </Header>
        {this.renderShowComponent(nodeKey, model, parents)}
      </Segment>
    );
  };

  renderShowComponent = (currentNode: string, model: Campaign, parents: string[]) => {
    if (!model || !model.nodes) return <div/>;
    const defaultProps = {model, currentNode};
    const warnings = _.get(this.props.warnings, currentNode, {});

    switch (model.nodes[currentNode].type) {
      case nodeTypes.scenarios:
        return (
          <ScenariosShow
            model={model}
            listRuleTypes={this.props.listRuleTypes}
            warnings={warnings}
            hideUsage={this.props.hideUsage}
            currentNode={currentNode}
            viewType={"view"}
          />
        );
      case nodeTypes.actions:
        return <ActionShow {...defaultProps} warnings={warnings} showSubActionDetail={true} hideUsage={this.props.hideUsage} parentKeyList={parents} listRuleTypes={this.props.listRuleTypes}/>;

      case nodeTypes.condition:
        return (
          <ConditionShow
            {...defaultProps}
            parentKeyList={parents}
            listRuleTypes={this.props.listRuleTypes}
          />
        );

      case nodeTypes.countCondition:
        return (
          <CountConditionShow
            {...defaultProps}
            parentNode={parents[parents.indexOf(currentNode) - 1]}
            parentKeyList={parents}
          />
        );

      case nodeTypes.count:
        return (
          <CounterShow
            {...defaultProps}
            parentNode={parents[0]}
            parentKeyList={parents}
          />
        );

      case nodeTypes.time:
        return (
          <TimeShow
            startTime={_.get(
              model.nodes[currentNode],
              'data.startTimePretty',
              '',
            )}
            endTime={_.get(model.nodes[currentNode], 'data.endTimePretty', '')}
            viewType="review"
          />
        );

      case nodeTypes.delay:
        return <DelayShow {...defaultProps} parentNode={parents[0]}/>;

      case nodeTypes.experiment:
        return (
          <ExperimentShow
            {...defaultProps}
          />
        );

      case nodeTypes.experimentTreatment:
        return (
          <ExperimentTreatmentShow
            {...defaultProps}
            parentNode={parents[parents.indexOf(currentNode) - 1]}
          />
        );

      case nodeTypes.randomizer:
        return (
          <RandomizerShow
            {...defaultProps}
            parentNode={parents[parents.indexOf(currentNode) - 1]}
          />
        );

      case nodeTypes.schedule:
        return (
          <TimeShow
            startTime={_.get(
              model.nodes[currentNode],
              'data.startTimePretty',
              '',
            )}
            endTime={_.get(model.nodes[currentNode], 'data.endTimePretty', '')}
            lastHappenedAt={_.get(model.nodes[currentNode], 'data.lastHappenedAtPretty', '')}
            frequency={_.get(model.nodes[currentNode], 'data.frequency', '')}
            viewType="review"
            nextTriggerDate={_.get(model.nodes[currentNode], 'data.nextTriggerTime')}
          />
        );

      case nodeTypes.enumeration:
        return (
          <EnumerationShow
            model={model}
            currentNode={currentNode}
            parentNode={parents[parents.indexOf(currentNode) - 1]}
          />
        );

      default:
        return <p>Unsupported node type {model.nodes[currentNode].type}</p>
    }
  };

  render() {
    const {campaign} = this.props;
    const treatmentStartKeysList = [];
    if (campaign && campaign.nodes) {
      const nodeKeys = _.keysIn(campaign.nodes);
      nodeKeys.forEach(nodeKey => {
        if ([nodeTypes.scenarios, nodeTypes.time, nodeTypes.schedule].includes(campaign.nodes[nodeKey].type)) {
          treatmentStartKeysList.push(nodeKey);
        }
      });
    }
    const finalData = treatmentStartKeysList.map(item => {
      return flatten(campaign.nodes, item, [parseInt(item, 10)], []);
    });

    return (
      <div>
        {(finalData || []).map((table, index) => {
          return (
            <Table celled structured key={`table${index}`}>
              <Table.Body>
                {(table || []).map((rows, rowIndex) => {
                  const parents = rows;
                  return (
                    <Table.Row key={`rows${rowIndex}`}>
                      {(rows || []).map(row => {
                        const rowSpan = spanOn(row, campaign.nodes);
                        if (rowSpan <= 0) {
                          return null;
                        }
                        return (
                          <Table.Cell rowSpan={rowSpan} key={row}>
                            {this.showData(
                              row.toString(),
                              campaign,
                              parents.map(item => Math.abs(item).toString())
                            )}
                          </Table.Cell>
                        );
                      })}
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          );
        })}
      </div>
    )
  }
}
